import { useEffect, useState } from "react";
import { Share2, Copy, Check, X as XIcon, MessageCircle, Send } from "lucide-react";
import { useTranslation } from "react-i18next";

const Facebook = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z" />
  </svg>
);

interface ShareButtonProps {
  url: string;
  title: string;
  text?: string;
}

export function ShareButton({ url, title, text }: ShareButtonProps) {
  const { i18n } = useTranslation();
  const isFrench = i18n.language.slice(0, 2).toLowerCase() === "fr";
  const labels = isFrench
    ? { share: "Partager", copied: "Copié", copy: "Copier" }
    : { share: "Compartir", copied: "Copiado", copy: "Copiar" };
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const fullUrl = url.startsWith("http") ? url : `${typeof window !== "undefined" ? window.location.origin : ""}${url}`;
  const message = `${title}${text ? ` — ${text}` : ""}`;

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
    } catch {
      const el = document.createElement("textarea");
      el.value = fullUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
    }
  }

  const nativeShare = async () => {
    if (typeof navigator !== "undefined" && (navigator as Navigator & { share?: (data: ShareData) => Promise<void> }).share) {
      try {
        await (navigator as Navigator & { share: (data: ShareData) => Promise<void> }).share({ title, text, url: fullUrl });
        return;
      } catch {
        /* ignore */
      }
    }
    setOpen((v) => !v);
  };

  const links = [
    {
      label: "WhatsApp",
      Icon: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(`${message} ${fullUrl}`)}`,
      cls: "bg-[#25D366] text-white",
    },
    {
      label: "Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`,
      cls: "bg-[#1877F2] text-white",
    },
    {
      label: "X",
      Icon: XIcon,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(fullUrl)}`,
      cls: "bg-black text-white",
    },
    {
      label: "Telegram",
      Icon: Send,
      href: `https://t.me/share/url?url=${encodeURIComponent(fullUrl)}&text=${encodeURIComponent(message)}`,
      cls: "bg-[#229ED9] text-white",
    },
  ];

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={nativeShare}
        className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
        aria-label={labels.share}
      >
        <Share2 className="h-4 w-4" />
        {labels.share}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 sm:bg-transparent"
            onClick={() => setOpen(false)}
          />
          <div
            role="dialog"
            aria-label={labels.share}
            className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-md rounded-t-2xl border border-border bg-card p-3 shadow-xl sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:mt-2 sm:w-72 sm:rounded-2xl sm:p-2"
          >
            <div className="flex flex-wrap items-start justify-center gap-3 p-2 sm:grid sm:grid-cols-4 sm:gap-2">
              {links.map(({ label, Icon, href, cls }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-16 shrink-0 flex-col items-center gap-1 text-[10px] font-semibold text-foreground/80"
                  onClick={() => setOpen(false)}
                >
                  <span className={`grid h-11 w-11 place-items-center rounded-full ${cls}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  {label}
                </a>
              ))}
            </div>
            <button
              type="button"
              onClick={copyLink}
              className="mt-1 flex w-full items-center justify-between gap-2 rounded-xl border border-border px-3 py-2 text-xs font-medium hover:bg-accent"
            >
              <span className="truncate text-muted-foreground">{fullUrl.replace(/^https?:\/\//, "")}</span>
              {copied ? (
                <span className="inline-flex items-center gap-1 text-success">
                  <Check className="h-3.5 w-3.5" /> {labels.copied}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <Copy className="h-3.5 w-3.5" /> {labels.copy}
                </span>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
